import type { Order } from '@keyvue/one-api-client-legacy';
import { User as LegacyUser } from '@keyvue/one-api-client-legacy';
import { computed, watch } from 'vue';
import type { Ref } from 'vue';
import { v4 as uuid } from 'uuid';
import type { User } from '@keyvue/one-api-client';

export default function useOrderCalculation(order: Ref<Order>) {
    const orderValue = computed(() => order.value.orderValue ?? 0);

    const teamShares = computed(() => order.value.orderValueShares.filter((share) => share.branch !== null));
    const userShares = computed(() => order.value.orderValueShares.filter((share) => share.user !== null));

    // Add default team share
    if (teamShares.value.length === 0) {
        order.value.orderValueShares.push({
            id: uuid(),
            branch: order.value.referral.referredByBranch,
            user: null,
            orderValue: 0,
            createdAt: new Date(),
            updatedAt: new Date(),
        });
    }

    // Add default user shares for involved users
    order.value.involvedUsers.forEach((user) => {
        if (userShares.value.some((share) => share.user?.id === user.id) === false) {
            order.value.orderValueShares.push({
                id: uuid(),
                branch: null,
                user,
                orderValue: 0,
                createdAt: new Date(),
                updatedAt: new Date(),
            });
        }
    });

    const teamTotalValue = computed(() => teamShares.value.reduce((acc, share) => acc + share.orderValue, 0));
    const teamTotalPercentage = computed(() => calculatePercentage(teamTotalValue.value, orderValue.value));
    const teamAvailableValue = computed(() => orderValue.value - teamTotalValue.value - userTotalValue.value);

    const userTotalValue = computed(() => userShares.value.reduce((acc, share) => acc + share.orderValue, 0));
    const userAvailableValue = computed(() => orderValue.value - teamTotalValue.value);
    const userTotalPercentage = computed(() => calculatePercentage(userTotalValue.value, userAvailableValue.value));

    const totalValue = computed(() => teamTotalValue.value + userTotalValue.value);
    const isAboveTotalValue = computed(() => totalValue.value > orderValue.value);
    const isBelowTotalValue = computed(() => totalValue.value < orderValue.value);
    const isAtTotalValue = computed(() => totalValue.value === orderValue.value);

    const evenlyDistributeUserShares = () => {
        const availableValue = userAvailableValue.value;
        const availableShares = userShares.value.length;

        if (availableShares === 0) {
            return;
        }

        const shareValue = floorToPrecision(availableValue / availableShares, 2);

        // eslint-disable-next-line no-param-reassign
        userShares.value.forEach((share) => { share.orderValue = shareValue; });

        if (teamAvailableValue.value !== 0) {
            userShares.value[0].orderValue = roundToPrecision(userShares.value[0].orderValue + teamAvailableValue.value, 2);
        }
    };

    watch([orderValue, teamTotalValue, userShares], evenlyDistributeUserShares);

    const calculatePercentage = (value: number, total: number) => {
        if (value === 0 || total === 0) {
            return 0;
        }

        const percentage = (value / total) * 100;

        return roundToPrecision(percentage, 2);
    };

    const addUserEntry = (user: User, value: number) => {
        order.value.orderValueShares.push({
            id: uuid(),
            branch: null,
            user: LegacyUser.fromOne(user),
            orderValue: value,
            createdAt: new Date(),
            updatedAt: new Date(),
        });
    };

    const removeUserEntry = (id: string) => {
        const index = order.value.orderValueShares.findIndex((share) => share.id === id);

        if (index === -1) {
            return;
        }

        order.value.orderValueShares.splice(index, 1);
    };

    const setFromPercentage = (id: string, percentage: number, total: number) => {
        const index = order.value.orderValueShares.findIndex((share) => share.id === id);

        if (index === -1) {
            return;
        }

        const share = order.value.orderValueShares[index];
        share.orderValue = roundToPrecision((total / 100) * percentage, 2);
    };

    const roundToPrecision = (number: number, precision: number) => {
        const factor = 10 ** precision;

        return Math.round(number * factor) / factor;
    };

    const floorToPrecision = (number: number, precision: number) => {
        const factor = 10 ** precision;

        return Math.floor(number * factor) / factor;
    };

    return {
        orderValue,

        teamShares,
        userShares,

        teamTotalValue,
        teamTotalPercentage,
        teamAvailableValue,

        userTotalValue,
        userTotalPercentage,
        userAvailableValue,

        totalValue,
        isAboveTotalValue,
        isBelowTotalValue,
        isAtTotalValue,

        evenlyDistributeUserShares,
        setFromPercentage,
        calculatePercentage,
        addUserEntry,
        removeUserEntry,
    };
}
